<template>
  <div>
    <el-card shadow="none" v-if="customer.bannerNote" class="warning-card"
      :body-style="{ padding: '10px 15px', textAlign: 'left' }">
      <i class="el-icon-warning" style="padding-right: 10px"></i>
      {{ customer.bannerNote }}
    </el-card>
    <el-card shadow="never" v-loading="loading">
      <el-alert v-if="error" :title="error" type="error"></el-alert>
      <el-dialog title="Update Primary Email" :visible.sync="editPrimaryEmailDialog" destroy-on-close>
        <el-form status-icon :model="primaryEmail" ref="primaryEmail">
          <el-form-item label="Email" prop="email" :rules="[
            { required: true, message: 'Please input email address', trigger: 'blur' },
            { type: 'email', message: 'Please input correct email address', trigger: ['blur', 'change'] },
            { validator: validateUniqEmail, trigger: 'blur' },
          ]">
            <el-input v-model="primaryEmail.email"></el-input>
          </el-form-item>
        </el-form>
        <el-button @click="updatePrimaryEmail">
          Save
        </el-button>
      </el-dialog>

      <div class="profile-wrapper">
        <div class="profile-buttons">
          <el-button icon="el-icon-edit" @click="routeToEditCustomer" type="secondary" size="small">
            Edit Profile
          </el-button>
          <el-button icon="el-icon-message" @click="openPrimaryEmailForm" type="secondary" size="small">
            Update Primary Email
          </el-button>
          <el-button :disabled="updatePasswordAlreadyRequested" @click="sendUpdatePasswordRequest" plain type="warning"
            size="small" icon="el-icon-refresh" :loading="passwordLoading">
            Reset Password
          </el-button>
          <el-button @click="sendWelcomeEmail" type="primary" size="small" icon="el-icon-user" :loading="welcomeLoading">
            Welcome Aboard
          </el-button>
        </div>

        <div class="profile-descriptions">
          <el-descriptions title="Company" :column="2">
            <el-descriptions-item label="Name">
              {{ customer.company }}
            </el-descriptions-item>
            <el-descriptions-item label="Tax ID">
              {{ customer.taxId }}
            </el-descriptions-item>
            <el-descriptions-item label="Tax Exemption">
              {{ customer.taxExempted ? 'Yes' : 'No' }}
            </el-descriptions-item>
            <el-descriptions-item label="Industry/Business">
              {{ customer.business }}
            </el-descriptions-item>
            <el-descriptions-item label="Salesforce/Member ID">
              {{ customer.salesforceId || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="Locations">
              {{ facilityNames }}
            </el-descriptions-item>
            <el-descriptions-item label="Past Locations" v-if="pastLocations.length">
              {{ pastLocationNames }}
            </el-descriptions-item>
          </el-descriptions>
          <el-descriptions title="Member Primary Contact" :column="2">
            <el-descriptions-item label="Email">
              {{ customer.email }}
              <i v-if="account && account.emailVerified" class="el-icon-success" :style="{ color: 'green' }">
              </i>
            </el-descriptions-item>
            <el-descriptions-item label="Full Name">
              {{
                customer.primaryContact
                ? customer.primaryContact.name
                : customer.fullName
              }}
            </el-descriptions-item>
            <el-descriptions-item label="Address Line 1">
              {{ customer.address }}
            </el-descriptions-item>
            <el-descriptions-item label="Address Line 2">
              {{ customer.address2 }}
            </el-descriptions-item>
            <el-descriptions-item label="Country">
              {{ customerCountry }}
            </el-descriptions-item>
            <el-descriptions-item label="City">
              {{ customer.city }}
            </el-descriptions-item>
            <el-descriptions-item label="State">
              {{ customer.state }}
            </el-descriptions-item>
            <el-descriptions-item label="Zip Code">
              {{ customer.zip }}
            </el-descriptions-item>
            <el-descriptions-item label="Phone Number">
              {{ customer.phone }}
            </el-descriptions-item>
          </el-descriptions>
          <el-divider> </el-divider>
          <el-descriptions title="Late fee" :column="3">
            <el-descriptions-item label="Chronic Late">
              <el-switch v-model="customer.chronicLate" active-text="Enable" inactive-text="Disable"
                active-color="#13ce66" inactive-color="#ff4949" @change="() => toggleChronicLate()">
              </el-switch>
            </el-descriptions-item>
          </el-descriptions>
          <el-divider> </el-divider>
          <el-descriptions title="Ban Member" :column="3">
            <el-descriptions-item>
              <el-switch
                v-model="ban"
                style="float: right; padding: 3px 0"
                active-color="#ff4949"
                active-text="Ban"
                inactive-color="#13ce66"
                @change="banCustomer"
                :disabled="loading"
              >
              </el-switch>
            </el-descriptions-item>
          </el-descriptions>
          <el-divider> </el-divider>
        </div>

        <div class="profile-id" :style="{}">
          <el-card shadow="never" :body-style="{ padding: '10px' }">
            <template slot="header">
              <el-row align="middle" type="flex" justify="space-between">
                <el-col :style="{ textAlign: 'left' }">
                  <strong>Insurance</strong>
                  <span v-if="facilityInsurances[0] && facilityInsurances[0].end">
                    (Until:
                    {{
                      facilityInsurances[0] && facilityInsurances[0].end
                      ? facilityInsurances[0].end
                      : ""
                      | formatDate }})
                  </span>
                </el-col>
                <el-col :span="6">
                  <el-link size="small" type="primary" @click="openInsuranceDialog" icon="el-icon-upload2">
                    Upload
                  </el-link>
                </el-col>
              </el-row>
            </template>

            <el-tabs v-model="activeTab">
              <el-tab-pane label="Active" name="active">
                <el-table :header-cell-style="{ padding: 0 }" :height="insurancesTableHeight"
                  :data="facilityInsurances" :default-sort="{ prop: 'end', order: 'descending' }">
                  <el-table-column property="end" label="Insured Until" sortable :width="130">
                    <template slot-scope="scope">
                      {{ scope.row.end | formatDate }}
                    </template>
                  </el-table-column>
                  <el-table-column property="createdAt" label="Uploaded At" sortable>
                    <template slot-scope="scope">
                      {{ new Date(scope.row.createdAt).toLocaleString('en-US') }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Actions" :width="70">
                    <template slot-scope="scope">
                      <a :href="scope.row.url">
                        <el-button size="mini" icon="el-icon-download"></el-button>
                      </a>
                    </template>
                  </el-table-column>
                </el-table>
              </el-tab-pane>
              <el-tab-pane label="Expired" name="expired">
                <el-table :header-cell-style="{ padding: 0 }" :data="facilityArchivedInsurances" :height="insurancesTableHeight">
                  <el-table-column property="expiry" label="Expired At" sortable>
                      <template slot-scope="scope">
                        {{ scope.row.expiry | formatDate }}
                      </template>
                    </el-table-column>
                    <el-table-column label="Actions" :width="70">
                      <template slot-scope="scope">
                        <a :href="scope.row.url">
                          <el-button size="mini" icon="el-icon-download"></el-button>
                        </a>
                      </template>
                    </el-table-column>
                </el-table>
              </el-tab-pane>
            </el-tabs>
          </el-card>

          <br />

          <el-card shadow="never" :body-style="{ padding: 0 }">
            <template slot="header">
              <el-row align="middle" type="flex" justify="space-between">
                <el-col :style="{ textAlign: 'left' }">
                  <strong>Identification</strong>
                </el-col>
                <el-col :span="6">
                  <el-link icon="el-icon-upload2" type="primary" size="small" @click="() => dialogFormIdentification = true">
                    Upload
                  </el-link>
                </el-col>
              </el-row>
            </template>
            <div :style="{background: 'white'}">
              <p v-if="customer.identification" class="id-name">
                Name: {{ customer.identification.name }}
              </p>
              <el-image v-if="customer.identification && customer.identification.url" :src="customer.identification.url">
              </el-image>
            </div>
          </el-card>
        </div>
      </div>
      <!-- <el-descriptions
          v-if="customer.alternateContactInformation"
          title="Alternate Contact Information"
        >
          <el-descriptions-item label="Contact">
            {{ customer.alternateContactInformation.contact }}
          </el-descriptions-item>
          <el-descriptions-item
            v-if="customer.alternateContactInformation.phone"
            label="Phone"
          >
            {{ customer.alternateContactInformation.phone }}
          </el-descriptions-item>
        </el-descriptions> -->
      <!-- <el-divider v-if="customer.taxId"></el-divider> -->
      <!-- <el-descriptions v-if="customer.taxId" title="Tax Info"> -->
      <!-- <el-descriptions-item label="Tax Exempt">
            {{ customer.notes.taxExempt }}
          </el-descriptions-item>
          <el-descriptions-item label="Late Fee Tax Exempt">
            {{ customer.notes.lateFeeExempt }}
          </el-descriptions-item> -->
        <!-- </el-descriptions> -->
        <el-dialog id="insurance" :visible.sync="dialogFormInsurance" destroy-on-close>
          <el-card>
            <el-form :model="insurance" ref="insuranceform">
              <el-form-item
                label="Upload Insurance"
                prop="url"
                :rules="[{ required: true, message: 'Please upload insurance' }]"
              >
                <Upload
                  @remove-upload="(_) => insurance.url = null"
                  @upload-completed="(url) => insurance.url = url"
                  :upload-prefix="`customer/${id}/`"
                />
              </el-form-item>
              <el-form-item
                label="Insurance End"
                prop="end"
                :rules="[{ required: true, message: 'Please add insurance end date', trigger: 'blur' }]">
                <el-date-picker
                  v-model="insurance.end"
                  placeholder="Insurance End"
                  :picker-options="insuranceEndDatePickerOptions"
                  type="date"
                >
                </el-date-picker>
              </el-form-item>
            </el-form>
            <el-button
              @click="addDocumentToCustomer"
            >
              <!-- :disabled="!insurance.end || !insurance.url" -->
              Save
            </el-button>
          </el-card>
        </el-dialog>
        <el-dialog id="identification" :visible.sync="dialogFormIdentification" destroy-on-close>
          <el-card>
            <el-form>
              <el-form-item label="Name">
                <el-input v-model="newIdentification.name"></el-input>
              </el-form-item>
            </el-form>
            <el-form-input label="Upload document">
              <Upload
                :upload-prefix="`customer/${id}/`"
                @upload-completed="(url) => newIdentification.url = url"
                list-type="picture"
              />
            </el-form-input>
            <el-button
              :disabled="!newIdentification.name || !newIdentification.url"
              @click="addIdentificationToCustomer"
            >
              Save
            </el-button>
          </el-card>
        </el-dialog>
    </el-card>
  </div>
</template>
<script>
import axios from 'axios';
import { CUSTOMER_API_BASE, AUTH_API_BASE, EMAIL_API_BASE, CUSTOMER_ADMIN_API_BASE, LEASING_API } from '../env';
import Upload from './Uploader.vue';
import NoSiteMap from './NoSiteMap.vue';
import { welcomeTemplate } from '../utils/emailTemplates';
import { getFacility, getLocationsForPortal } from '../utils/commonApi';
import randomCode from '../utils/random';
import { allowAccess } from '../utils/roles';
import { getCustomerById } from '../api/customers';
import { deleteCustomerPurgedDocuments, createCustomerDocument } from '../api/documents';

export default {
  name: 'Customer',
  props: {
    customerDetails: { type: Object, default: () => {} },
    id: { type: String, required: true },
    facility: { type: String, default: null },
  },
  components: {
    Upload,
    NoSiteMap,
  },
  data() {
    const insuranceEndDateValidator = date => date < new Date();

    return {
      primaryContact: {
        password: '',
      },
      ban: false,
      activeTab: 'active',
      facilityNames: [],
      customer: {},
      passwordLoading: false,
      welcomeLoading: false,
      facilityInsurances: [],
      facilityArchivedInsurances: [],
      facilityData: {},
      editPrimaryEmailDialog: false,
      primaryEmail: { email: '' },
      loading: false,
      account: null,
      error: '',
      insuranceEndDatePickerOptions: {
        disabledDate: insuranceEndDateValidator,
      },
      dialogFormInsurance: false,
      dialogFormIdentification: false,
      newIdentification: {
        name: '',
        url: '',
      },
      insurance: {
        end: null,
        url: null,
        facility: null,
      },
      insuranceDocument: {
        url: null,
        createdAt: new Date(),
        createdBy: '',
      },
      newAccount: {
        createdBy: '',
        email: '',
        name: '',
        access: '',
      },
      activeAndFutureLeases: [],
      pastLeases: [],
      pastLocationNames: [],
    };
  },
  async mounted() {
    try {
      this.loading = true;
      await this.getCustomer();
      await this.getCustomerActiveAndFutureLeases();
      await this.getCustomerPastLeases();
      await this.getFacilityData();
      await this.getCrmAccount();

      this.ban = this.customer.ban;
      this.loading = false;
    } catch (err) {
      this.loading = false;
      if (err.response && err.response.data) {
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });

        if (err.response.data === 'access_denied') {
          this.$router.push({ name: '404' });
        }
      }
    }
  },
  computed: {
    pastLocations() {
      const activeLocations = this.activeAndFutureLeases.map(lease => lease.facility._id);
      const pastLeasesWithoutActiveLease = this.pastLeases.filter(lease => !activeLocations.includes(lease.facility));
      return pastLeasesWithoutActiveLease.map(lease => lease.facility);
    },
    insurancesTableHeight() {
      const numFacilityInsurances = this.facilityInsurances ? this.facilityInsurances.length : 0;
      const numFacilityArchivedInsurances = this.facilityArchivedInsurances ? this.facilityArchivedInsurances.length : 0;

      const maxCount = Math.max(numFacilityInsurances, numFacilityArchivedInsurances);
      const height = maxCount === 1 ? 115 : 250;

      return height;
    },
    updatePasswordAlreadyRequested() {
      if (!this.account) { return true; }

      return this.account.requiredActions.includes('UPDATE_PASSWORD');
    },
    customerCountry() {
      if (this.customer.country === 'us') {
        return 'United States of America';
      } else if (this.customer.country === 'ca') {
        return 'Canada';
      } else if (this.customer.country === 'mx') {
        return 'Mexico';
      } else if (this.customer.country === 'pr') {
        return 'Puerto Rico';
      }
      return 'Bahamas';
    },
  },
  watch: {
    async facility() {
      this.loading = true;
      this.customer.insurances = [];
      await this.getCustomer();
      await this.getFacilityData();
      await this.getCrmAccount();
      this.loading = false;
    },
    async id() {
      try {
        this.loading = true;
        this.customer = {};
        this.customer.company = '';
        await this.getCustomer();
        await this.getCustomerActiveAndFutureLeases();
        await this.getCustomerPastLeases();
        await this.getFacilityData();
        await this.getCrmAccount();
        this.loading = false;
      } catch (err) {
        this.loading = false;
        console.log(err);
      }
    },
  },
  methods: {
    async setPassword() {
      try {
        const code = randomCode();
        this.primaryContact.password = code;
        await axios.put(`${AUTH_API_BASE}/customer/${this.account.id}/reset-password`, {
          value: this.primaryContact.password,
          temporary: true,
          type: 'password',
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.message ? err.message : err.response.data,
        });
      }
    },
    async banCustomer() {
      this.loading = true;
      await axios.post(`${CUSTOMER_ADMIN_API_BASE}/${this.customer._id}/ban`, {
        ban: this.ban,
      }, { headers: {
        Authorization: `Bearer ${this.$keycloak.token}`,
      } });
      this.loading = false;
    },
    async sendWelcomeEmail() {
      this.welcomeLoading = true;
      await this.setPassword();
      try {
        const mjmlTemplate = welcomeTemplate(
          this.account.firstName, this.account.email, this.primaryContact.password,
        );
        await axios.post(`${EMAIL_API_BASE}/send/email`, {
          from: this.$keycloak.tokenParsed.email,
          to: this.customer.email,
          customer: this.id,
          facility: this.facility,
          subject: 'Welcome to ReadySpaces',
          mjml: true,
          body: mjmlTemplate,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.$message.success({
          title: 'Success',
          message: 'Email sent successfully',
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: 'Send email failed',
        });
      }
      this.welcomeLoading = false;
    },
    async sendUpdatePasswordRequest() {
      this.passwordLoading = true;
      try {
        await axios.put(`${AUTH_API_BASE}/customers/${this.account.id}/execute-actions-email`,
          ['UPDATE_PASSWORD'], {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          });

        this.$notify.success({
          title: 'Success',
          message: 'Success! Password reset email sent.',
        });
      } catch (err) {
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
      this.passwordLoading = false;
    },
    async getCrmAccount() {
      const response = await axios.get(`${AUTH_API_BASE}/customers`, {
        params: {
          email: this.customer.email,
        },
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.account = response.data && response.data.length > 0 ? response.data[0] : null;
    },
    openPrimaryEmailForm() {
      this.primaryEmail.email = this.customer.email;
      this.editPrimaryEmailDialog = true;
    },
    async validateUniqEmail(_rule, _value, callback) {
      if (this.primaryEmail.email === this.customer.email) return callback();

      const emailResponse = await this.getEmail(this.primaryEmail.email);
      // Allow changing to an already existing email if the customer id matches
      if (emailResponse.length && emailResponse[0].attributes.customerId[0] !== this.id) {
        return callback(new Error('Email Already Exists'));
      }

      return callback();
    },
    async getCustomer() {
      const customer = await getCustomerById(this.id, this.facility, this.$keycloak.token);
      this.customer = { ...customer };
      this.facilityInsurances = this.customer.insurances.filter(insurance => insurance.facility === this.facility)
        .sort((a, b) => new Date(b.end) - new Date(a.end));
      this.facilityArchivedInsurances = this.customer.archivedInsurances.filter(insurance => insurance.facility === this.facility)
        .sort((a, b) => new Date(b.end) - new Date(a.end));
    },
    async updatePrimaryEmail() {
      this.$refs.primaryEmail.validate(async (valid) => {
        if (valid) {
          const emailResponse = await this.getEmail(this.primaryEmail.email);
          // Allow changing to already existing email if the customer id matches
          if (emailResponse.length && emailResponse[0].attributes.customerId[0] !== this.id) {
            this.$message.error({
              title: 'Email Error',
              message: 'Email Already Exists',
            });
            return;
          }

          await this.getCustomer();

          const wfFound = this.customer.wfs.filter(wf => wf.name === 'updatePrimaryEmail');
          if (wfFound.length) {
            this.$message.error({
              message: 'Slow down! A Background update job is already running',
            });
            return;
          }

          const { email } = this.primaryEmail;
          try {
            const response = await axios.put(`${CUSTOMER_ADMIN_API_BASE}/${this.id}/primary/email/wf`, {
              email,
              createdBy: this.$keycloak.tokenParsed.email,
            }, {
              headers: {
                Authorization: `Bearer ${this.$keycloak.token}`,
              },
            });

            this.customer = response.data;

            this.$message.success({
              message: 'Email Background job started successfully',
            });

            this.editPrimaryEmailDialog = false;
          } catch (e) {
            this.$message.error({
              message: 'There was an error updating the email.',
            });
          }
        }
      });
    },
    async getEmail(email) {
      const response = await axios.get(`${AUTH_API_BASE}/customers`, {
        params: {
          email,
        },
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      return response.data;
    },
    allowAccess(role) {
      return allowAccess(role, this.$keycloak, this.$root.state.facility);
    },
    async getFacilityData() {
      if (this.facility) {
        this.facilityData = await getFacility(this.facility, {
          token: this.$keycloak.token,
        });

        const locations = await getLocationsForPortal(this.$keycloak.token);

        const activeAndFutureLocations = this.activeAndFutureLeases.map(lease => lease.facility._id);
        this.facilityNames =
          locations
            .filter(facilityData => (this.pastLocations.length ? activeAndFutureLocations.includes(facilityData._id) : this.customer.locations.includes(facilityData._id)))
            .map(facilityData => facilityData.title)
            .join(', ');
        this.pastLocationNames =
          locations
            .filter(facilityData => this.pastLocations.includes(facilityData._id))
            .map(facilityData => facilityData.title)
            .join(', ');
      }
    },
    async getLocationPastLeases(location) {
      const response = await axios.get(`${LEASING_API}/v1/${location}/${this.id}/past/lease`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });

      return response.data;
    },
    async getLocationActiveAndFutureLeases(location) {
      const response = await axios.get(
        `${LEASING_API}/v1/customer/${this.id}/leases`,
        {
          params: {
            facility: location,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );

      return response.data;
    },
    async getCustomerPastLeases() {
      await Promise.all(this.customer.locations.map(location => this.getLocationPastLeases(location).then(response => this.pastLeases = [...this.pastLeases, ...response])));
    },
    async getCustomerActiveAndFutureLeases() {
      await Promise.all(this.customer.locations.map(location => this.getLocationActiveAndFutureLeases(location).then(response => this.activeAndFutureLeases = [...this.activeAndFutureLeases, ...response])));
    },
    async addDocumentToCustomer() {
      this.$refs.insuranceform.validate(async (valid) => {
        if (!valid) return;

        this.loading = true;
        try {
          await createCustomerDocument(
            this.customer._id,
            {
              url: this.insurance.url,
              end: this.insurance.end,
              createdBy: this.$keycloak.tokenParsed.email,
              type: 'INSURANCE',
              facility: this.facility,
            },
            false,
            this.$keycloak.token,
          );
        } catch (e) {
          this.dialogFormInsurance = false;
          this.loading = false;
          console.log(e);
          this.$notify.error({
            title: 'Error',
            message: 'Unable to add Insurance.',
          });
          return;
        }
        deleteCustomerPurgedDocuments(
          this.customer._id,
          this.facility,
          this.$keycloak.token,
        );
        this.dialogFormInsurance = false;
        this.loading = false;
        this.$notify.success({
          title: 'Success',
          message: 'Insurance added successfully.',
        });

        await this.getCustomer();
      });
    },
    async addIdentificationToCustomer() {
      this.loading = true;
      await axios.put(
        `${CUSTOMER_API_BASE}/customers/v1/${this.customer._id}`,
        {
          identification: {
            ...this.newIdentification,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );

      this.customer.identification = this.newIdentification;
      this.dialogFormIdentification = false;
      this.loading = false;
      this.$notify.success({
        title: 'Success',
        message: 'Identification added successfully.',
      });
    },
    openInsuranceDialog() {
      this.dialogFormInsurance = true;

      if (this.customer.insurances && this.customer.insurances.length === 0 && this.customer.insurance) {
        this.insuranceDocument.url = this.customer.insurance;
        this.customer.insurances = [{ ...this.insuranceDocument }];
      }
    },
    routeToEditCustomer() {
      if (this.facility) {
        this.$router.push({
          name: 'Facility Customer Edit',
          params: { customerId: this.id, facility: this.facility },
        });
        return;
      }
      this.$router.push({
        name: 'Customer Edit',
        params: { customerId: this.id },
      });
    },
    async toggleChronicLate() {
      this.loading = true;
      const response = await axios.put(`${CUSTOMER_API_BASE}/customers/v1/${this.id}`, {
        chronicLate: this.customer.chronicLate,
      }, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.customer = response.data;
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.id-name {
  text-align: start;
  padding-left: 1rem;
}

.list {
  list-style-type: none;
  text-align: left;
}

.list li {
  padding: 10px;
}

.data {
  text-align: left;
}

.card {
  padding: 10px;
  margin: 10px;
}

.header {
  background-color: crimson;
  color: white;
}

.text {
  font-size: 14px;
  list-style-type: square;
  text-align: left;
}

.item {
  margin-bottom: 18px;
}

.voided {
  background-color: crimson;
}

.warning-card {
  background-color: #fdf6ec;
  border-color: #e6a23c;
  color: #e6a23c;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.profile-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.profile-buttons {
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 0.5rem;
  width: 100%;
}

.profile-buttons .el-button+.el-button {
  margin-left: 0;
}

.profile-descriptions {
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .profile-buttons {
    grid-column: 1 / 6;
    flex-direction: row;
    justify-content: end;
  }

  .profile-wrapper {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
  }

  .profile-descriptions {
    grid-column: 1 / 4;
  }

  .profile-id {
    grid-column: 4 / 6;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CustomerProfile.vue